"use client"
/**
 * Activity Wise Requirements Page
 * Route: /requirements/activity-wide
 * Shows the Activities table; clicking Requirements icon opens a drawer
 * that shows ONLY the "Activity Wise" requirements for that activity.
 */
import React, { useState, useEffect } from 'react'
import { useConfirm } from '../../provider/confirm.provider'
import { FaLayerGroup, FaClipboardList } from 'react-icons/fa'
import { FiFileText, FiRefreshCw, FiX, FiCheck, FiPlus, FiAlertCircle } from 'react-icons/fi'
import { ActivitiesManagement } from '../activities'
import axiosInstance from '../../libs/axios'
import { useAuth } from '../../provider/auth.provider'
import toast from 'react-hot-toast'

interface Activity {
  id: number
  activity_name: string
  activity_description: string
  status: string
}

interface Requirement {
  id: number
  title: string
  content: string
  requirement_type: 'project' | 'activity' | 'common'
  project_id: number | null
  activity_id: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  activity?: { id: number; activity_name: string }
  creator?: { id: number; first_name: string; last_name: string }
  updater?: { id: number; first_name: string; last_name: string }
}

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

// ── Activity Requirements Drawer ─────────────────────────────────────────────

interface ActivityRequirementsDrawerProps {
  isOpen: boolean
  onClose: () => void
  activity: Activity | null
}

const ActivityRequirementsDrawer: React.FC<ActivityRequirementsDrawerProps> = ({ isOpen, onClose, activity }) => {
  const { hasPermission, user } = useAuth()
  const { confirm } = useConfirm()
  const canCreate = hasPermission('OperationsRequirementsProjectActivity', 'create_activity_wise')
  const canEdit   = hasPermission('OperationsRequirementsProjectActivity', 'edit_activity_wise') && user?.role.id !== 7
  const canDelete = hasPermission('OperationsRequirementsProjectActivity', 'delete_activity_wise')

  const [requirements, setRequirements] = useState<Requirement[]>([])
  const [loading, setLoading] = useState(false)

  // Create form
  const [isCreating, setIsCreating] = useState(false)
  const [newTitle, setNewTitle]     = useState('')
  const [newContent, setNewContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Edit state
  const [editingReq, setEditingReq]     = useState<Requirement | null>(null)
  const [editTitle, setEditTitle]       = useState('')
  const [editContent, setEditContent]   = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)

  const fetchRequirements = async () => {
    if (!activity) return
    try {
      setLoading(true)
      const res = await axiosInstance.get('/requirements', { params: { is_active: true } })
      const all: Requirement[] = res.data.data.requirements || []
      // Only activity-wide for this activity
      setRequirements(all.filter(r => r.requirement_type === 'activity' && r.activity_id === activity.id))
    } catch { toast.error('Failed to load requirements') }
    finally { setLoading(false) }
  }

  useEffect(() => {
    if (isOpen && activity) {
      fetchRequirements()
      setIsCreating(false); setNewTitle(''); setNewContent('')
      setEditingReq(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activity])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim() || !newContent.trim()) { toast.error('Title and content required'); return }
    if (!activity) return
    try {
      setSubmitting(true)
      await axiosInstance.post('/requirements', { title: newTitle.trim(), content: newContent.trim(), requirement_type: 'activity', activity_id: activity.id })
      toast.success('Requirement created')
      setIsCreating(false); setNewTitle(''); setNewContent('')
      fetchRequirements()
    } catch {} finally { setSubmitting(false) }
  }

  const startEdit = (req: Requirement) => { setEditingReq(req); setEditTitle(req.title); setEditContent(req.content) }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTitle.trim() || !editContent.trim() || !editingReq) return
    try {
      setEditSubmitting(true)
      await axiosInstance.put(`/requirements/${editingReq.id}`, { title: editTitle.trim(), content: editContent.trim() })
      toast.success('Requirement updated')
      setEditingReq(null)
      fetchRequirements()
    } catch {} finally { setEditSubmitting(false) }
  }

  const handleDelete = async (req: Requirement) => {
    const ok = await confirm({
      title: "Delete requirement",
      message: `Delete "${req.title}"? This cannot be undone.`,
      confirmText: "Delete requirement",
      variant: "danger",
    })
    if (!ok) return
    try { await axiosInstance.delete(`/requirements/${req.id}`); fetchRequirements() } catch {}
  }

  if (!isOpen || !activity) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FaClipboardList className="text-orange-600" size={18} />
              Activity Wise Requirements
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 font-medium text-emerald-700">{activity.activity_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && !isCreating && !editingReq && (
              <button onClick={() => setIsCreating(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-800 text-white rounded-lg text-xs font-medium hover:bg-blue-900">
                <FiPlus size={13} /> Add
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg"><FiX size={18} className="text-gray-500" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Edit form */}
          {editingReq && (
            <form onSubmit={handleUpdate} className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-gray-900 text-sm">Edit Requirement</h4>
                <button type="button" onClick={() => setEditingReq(null)} className="p-1 hover:bg-blue-100 rounded"><FiX size={14} /></button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details *</label>
                <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={5} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 resize-y" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditingReq(null)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">Cancel</button>
                <button type="submit" disabled={editSubmitting} className="px-4 py-1.5 text-xs font-medium text-white bg-blue-800 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                  {editSubmitting ? <FiRefreshCw size={12} className="animate-spin" /> : <FiCheck size={12} />} Update
                </button>
              </div>
            </form>
          )}

          {/* Create form */}
          {isCreating && !editingReq && (
            <form onSubmit={handleCreate} className="border-2 border-black-200 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-bold text-gray-900 text-sm">New Activity Wise Requirement</h4>
                <button type="button" onClick={() => setIsCreating(false)} className="p-1 hover:bg-emerald-100 rounded"><FiX size={14} /></button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Title *</label>
                <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Requirement title..." className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Details *</label>
                <textarea value={newContent} onChange={e => setNewContent(e.target.value)} placeholder="Describe the requirement..." rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-emerald-500 resize-y" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setIsCreating(false)} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">Cancel</button>
                <button type="submit" disabled={submitting} className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm">
                  {submitting ? <FiRefreshCw size={12} className="animate-spin" /> : <FiCheck size={12} />} Save
                </button>
              </div>
            </form>
          )}

          {/* Requirements list */}
          {loading ? (
            <div className="text-center py-10"><FiRefreshCw className="animate-spin mx-auto text-blue-800 mb-2" size={24} /><p className="text-sm text-gray-500">Loading...</p></div>
          ) : requirements.length === 0 && !isCreating ? (
            <div className="text-center py-10">
              <FiAlertCircle className="mx-auto text-gray-300 mb-2" size={36} />
              <p className="text-sm font-semibold text-gray-700">No activity-wide requirements</p>
              <p className="text-xs text-gray-400 mt-1">{canCreate ? 'Click "Add" to create one.' : 'None assigned.'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requirements.map(req => (
                <div key={req.id} className={`bg-white border rounded-xl p-4 ${req.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 text-sm mb-1">{req.title}</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{req.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {req.creator && <span>By {req.creator.first_name} {req.creator.last_name}</span>}
                        <span>Created: {formatDate(req.created_at)}</span>
                        <span>Updated: {formatDate(req.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {canEdit && <button onClick={() => startEdit(req)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-700" title="Edit"><FiFileText size={14} /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const ActivityWidePage: React.FC = () => {
  const [drawerOpen, setDrawerOpen]     = useState(false)
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null)

  // Intercept the edit action from ActivitiesManagement
  const [interceptedActivity, setInterceptedActivity] = useState<Activity | null>(null)

  return (
    <div>

      {/* Activities table — edit button opens requirements drawer */}
      <ActivitiesManagement
        onEditActivityOverride={(activity: Activity) => {
          setSelectedActivity(activity)
          setDrawerOpen(true)
        }}
        hideAddActivityButton={true}
      />

      {/* Activity Requirements drawer — activity-wide only */}
      <ActivityRequirementsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activity={selectedActivity}
      />
    </div>
  )
}

export default ActivityWidePage
